import { NestAPI } from '@marketing-milk/frontend'
import {
  Comment,
  CommentAnchorType,
  CreateComment,
  UpdateComment,
} from '@marketing-milk/interfaces'
import { useQuery, useQueryClient } from 'react-query'

interface CreateInput {
  body: string
  parentCommentID?: number | null
  taggedUserIDs?: number[]
}

export const commentsQueryKey = (
  businessID: number,
  anchorType: CommentAnchorType,
  anchorID: string | null
) => ['comments', businessID, anchorType, anchorID] as const

export const useComments = (
  businessID: number,
  anchorType: CommentAnchorType,
  anchorID: string | null
) => {
  const queryClient = useQueryClient()
  const endpoint = `businesses/${businessID}/comments`

  const {
    data: comments = [],
    refetch,
    isLoading,
    isFetched,
    isError,
  } = useQuery<Comment[], Error>(
    commentsQueryKey(businessID, anchorType, anchorID),
    async () => {
      const response = await NestAPI.get<Comment[]>(
        `${endpoint}?anchorType=${anchorType}&anchorID=${encodeURIComponent(anchorID ?? '')}`
      )
      return response.data
    },
    {
      enabled: Boolean(anchorID),
      refetchOnWindowFocus: true,
      staleTime: 30_000,
    }
  )

  const invalidate = () =>
    queryClient.invalidateQueries(commentsQueryKey(businessID, anchorType, anchorID))

  const create = async (input: CreateInput): Promise<Comment | undefined> => {
    if (!anchorID) return undefined
    const dto: CreateComment = {
      anchorType,
      anchorID,
      body: input.body,
      parentCommentID: input.parentCommentID ?? null,
      taggedUserIDs: input.taggedUserIDs,
    }
    const response = await NestAPI.post<Comment>(endpoint, dto)
    await invalidate()
    return response.data
  }

  const update = async (commentID: number, dto: UpdateComment): Promise<Comment | undefined> => {
    const response = await NestAPI.patch<Comment>(`${endpoint}/${commentID}`, dto)
    await invalidate()
    return response.data
  }

  const resolve = (commentID: number) => update(commentID, { resolved: true })
  const unresolve = (commentID: number) => update(commentID, { resolved: false })

  const remove = async (commentID: number): Promise<void> => {
    await NestAPI.delete(`${endpoint}/${commentID}`)
    await invalidate()
  }

  return {
    comments,
    isLoading,
    isFetched,
    isError,
    refetch,
    create,
    update,
    resolve,
    unresolve,
    remove,
  }
}
